import { useEffect } from 'react'

import { Gutter } from '@/app/_components/Gutter/index.jsx'

import { CmsCalculator_CalculatorType as CmsCalculatorType } from '../../lib/cms/generated/graphql.js'
import { cmsGraphql, FragmentType, getFragmentData } from '../../lib/cms/index.js'

export const calculatorFragment = cmsGraphql(/* GraphQL */ `
  fragment calculatorFragment on CmsCalculator {
    calculatorType
  }
`)

export const calculatorTypename = 'CmsCalculator'

export type CalculatorProps = {
  data: FragmentType<typeof calculatorFragment>
}

const scriptLookup: Record<CmsCalculatorType, string> = {
  term_loan_apr: 'embed_term_loan_calc.js',
  invoice_financing_apr: 'embed_factoring_calc.js',
  mca_apr: 'embed_mca_calc_v2.js',
  ondeck_apr: 'embed_ondeck_calc.js',
}

const nameLookup: Record<CmsCalculatorType, string> = {
  term_loan_apr: 'Term Loan APR Calculator',
  invoice_financing_apr: 'Invoice Financing APR Calculator',
  mca_apr: 'Merchant Cash Advance APR Calculator',
  ondeck_apr: 'OnDeck APR Calculator',
}

const instructionsLookup: Record<CmsCalculatorType, string> = {
  term_loan_apr: `<!-- Start https://www.nav.com Term Loan APR Calculator -->
<div id="embedded-calc" class="embedded-calc">
<div class="powered-by">Powered by <a href="https://www.nav.com">nav</a></div>
</div>
<script src="https://www.nav.com/calculators/embed_term_loan_calc.js"></script>
<!-- End https://www.nav.com Term Loan APR Calculator -->`,
  invoice_financing_apr: `<!-- Start https://www.nav.com Invoice Financing APR Calculator -->
<div id="embedded-calc" class="embedded-calc">
<div class="powered-by">Powered by <a href="https://www.nav.com">nav</a></div>
</div>
<script src="https://www.nav.com/calculators/embed_factoring_calc.js"></script>
<!-- End https://www.nav.com Invoice Financing APR Calculator -->`,
  mca_apr: `<!-- Start https://www.nav.com Merchant Cash Advance APR Calculator -->
<div id="embedded-calc-mca" class="embedded-calc">
<div class="powered-by">Powered by <a href="https://www.nav.com">nav</a></div>
</div>
<script src="https://www.nav.com/calculators/embed_mca_calc_v2.js"></script>
<!-- End https://www.nav.com Merchant Cash Advance APR Calculator -->`,
  ondeck_apr: `<!-- Start https://www.nav.com OnDeck APR Calculator -->
<div id="embedded-calc" class="embedded-calc">
<div class="powered-by">Powered by <a href="https://www.nav.com">nav</a></div>
</div>
<script src="https://www.nav.com/calculators/embed_ondeck_calc.js"></script>
<!-- End https://www.nav.com OnDeck APR Calculator -->`,
}

const CalculatorComponent = ({ type }: { type: CmsCalculatorType }) => {
  useEffect(() => {
    const scriptId = `calculator-script-${type}`

    // Check if the script is already added to avoid duplicates
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script')
      script.id = scriptId
      script.src = `/calculators/${scriptLookup[type]}`
      script.async = true
      document.body.appendChild(script)

      // Cleanup: Remove the script when the component is unmounted
      return () => {
        document.getElementById(scriptId)?.remove()
      }
    }
  }, [type])

  const id = type === 'mca_apr' ? 'embedded-calc-mca' : 'embedded-calc'
  const calculatorName = nameLookup[type]
  const instructions = instructionsLookup[type]

  return (
    <>
      <div id={id} className="embedded-calc">
        <div className="powered-by">
          Powered by <a href="https://www.nav.com">nav</a>
        </div>
      </div>

      <h3 className="title1-emphasized my-200">
        Add the {calculatorName} to your website, copy &amp; paste the code below
      </h3>
      <pre className="body2 mb-150 rounded-100 border border-solid border-[#ccc] bg-[#f5f5f5] p-150">
        {instructions}
      </pre>
    </>
  )
}

export const Calculator = ({ data }: CalculatorProps) => {
  const { calculatorType } = getFragmentData(calculatorFragment, data)

  if (!calculatorType) {
    return null
  }

  return (
    <Gutter className="my-200 px-0">
      <CalculatorComponent type={calculatorType} />
    </Gutter>
  )
}
